import { FC, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { SelectorOption } from 'types';
import { useGetActualSociety } from 'hooks/useGetActualSociety';

import Page from 'modules/_shared/components/Pages/Page';
import CustomLoading from 'modules/_shared/components/CustomLoading';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { useGetEnvelopesList } from './hooks';

import EnvelopesListHeader from './components/EnvelopesListHeader';
import EnvelopesListBanner from './components/EnvelopesListBanner';
import EnvelopesListSummarySection from './components/EnvelopesListSummarySection';
import EnvelopesListRecentsSection from './components/EnvelopesListRecentsSection';

const EnvelopesList: FC = () => {
  const { societyId } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [status, setStatus] = useState<SelectorOption | null>(null);
  const [signatureType, setSignatureType] = useState<SelectorOption | null>(
    null
  );

  const filters = useMemo(
    () => ({
      status: status?.value,
      signatureType: signatureType?.value,
    }),
    [status?.value, signatureType?.value]
  );

  const {
    data: { envelopes, envelopesTotal },
    isLoading,
    refetch,
  } = useGetEnvelopesList(
    societyId,
    {
      page: currentPage,
      limit: rowsPerPage,
    },
    filters
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleRowsPerPageChange = useCallback((limit: number) => {
    setRowsPerPage(limit);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = (option: SelectorOption | null) => {
    setStatus(option);
    setCurrentPage(1);
  };

  const handleSignatureTypeChange = (option: SelectorOption | null) => {
    setSignatureType(option);
    setCurrentPage(1);
  };

  useGetMenu();
  useGetActualSociety(societyId);

  return (
    <Page id="envelopes-list-page">
      <EnvelopesListHeader
        envelopesTotal={envelopesTotal}
        onRefresh={refetch}
      />

      <EnvelopesListSummarySection />

      <EnvelopesListRecentsSection
        envelopes={envelopes}
        envelopesTotal={envelopesTotal}
        serverPage={currentPage}
        serverRowsPerPage={rowsPerPage}
        onServerPageChange={handlePageChange}
        onServerRowsPerPageChange={handleRowsPerPageChange}
        onRefresh={refetch}
        status={status}
        signatureType={signatureType}
        onStatusChange={handleStatusChange}
        onSignatureTypeChange={handleSignatureTypeChange}
      />
      <EnvelopesListBanner />

      {isLoading && <CustomLoading />}
    </Page>
  );
};

export default EnvelopesList;
